import { Box, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import { Check, FileCheck, KeyRound, Lock, ShieldCheck, ShieldQuestion } from 'lucide-react'
import { PageHeader, SectionHeading } from '@/components/shell'
import { TlsHandshakeSimulation } from '@/components/TlsHandshakeSimulation'

export const Route = createFileRoute('/_app/education/tls-mtls')({
  component: TlsMtlsPage,
})

interface PhaseCardProps {
  icon: typeof Lock
  phase: string
  title: string
  description: string
}

function PhaseCard({ icon: Icon, phase, title, description }: PhaseCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ shadow: 'md' }}>
      <Card.Body p={5} mt="4" mb="4">
        <VStack align="start" gap={3}>
          <HStack gap={3}>
            <Box p={2} bg="brand.solid" borderRadius="lg">
              <Icon size={20} />
            </Box>
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="content.secondary"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {phase}
            </Text>
          </HStack>
          <VStack align="start" gap={1}>
            <Text fontWeight="medium">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

function TlsMtlsPage() {
  const phases: PhaseCardProps[] = [
    {
      icon: ShieldCheck,
      phase: 'Server authentication',
      title: 'The broker proves who it is',
      description:
        'HiveMQ presents its server certificate and private key from its keystore. The client only trusts it if the dialed hostname matches a SAN entry and the CA chain is valid.',
    },
    {
      icon: Lock,
      phase: 'Encrypted channel',
      title: 'One-way TLS is established',
      description:
        'Session keys are agreed and all traffic is encrypted. The client now trusts the broker - but the broker still has no idea who the client is.',
    },
    {
      icon: KeyRound,
      phase: 'Client authentication',
      title: 'The client proves who it is (mTLS)',
      description:
        'With mutual TLS enabled, the broker requests a certificate; the client presents its own certificate from its keystore and proves it holds the matching private key.',
    },
    {
      icon: FileCheck,
      phase: 'Issuer validation',
      title: 'The broker validates the client via its truststore',
      description:
        'HiveMQ checks the client certificate against its truststore (root + intermediate CA). A cert issued by a trusted CA is accepted; a cert from any other CA is rejected outright.',
    },
  ]

  const takeaways = [
    {
      title: 'Keystore proves identity, truststore verifies others',
      description:
        'Each side presents its own certificate from its keystore. The broker verifies the client against its truststore, which holds the trusted root and intermediate CA.',
    },
    {
      title: 'Server trust needs a matching SAN + valid chain',
      description:
        'The client only trusts the broker when the hostname it dialed matches a SAN entry on the server certificate and the certificate chain validates.',
    },
    {
      title: 'mTLS is issuer-only trust',
      description:
        'Any certificate signed by the trusted CA chain is accepted - the issuer is validated, not a specific client. A certificate from any other CA is refused.',
    },
    {
      title: 'Restrict specific clients with an identity layer',
      description:
        "To go beyond issuer trust and authorize specific clients, add an identity layer - e.g. HiveMQ's certificate-to-identity mapping or an authentication extension.",
    },
    {
      title: 'Untrusted certificates abort the handshake',
      description:
        'If the client certificate’s issuer is not in the truststore, the broker aborts the TLS handshake and closes the connection - the client never reaches the MQTT session.',
    },
    {
      title: 'Conceptual TLS 1.2 flow',
      description:
        'The animation follows the message order of TLS 1.2. TLS 1.3 reorders and encrypts some messages, but the role of the keystore and truststore is identical.',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="TLS & mutual TLS on the HiveMQ broker"
        subtitle="How an MQTT client and the broker authenticate each other using a Java keystore and truststore."
      />

      <VStack align="stretch" gap={10}>
        {/* Tags */}
        <HStack gap={2} flexWrap="wrap" justify="center">
          {['TLS', 'mutual TLS', 'Keystore', 'Truststore'].map((tag) => (
            <Box key={tag} px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                {tag}
              </Text>
            </Box>
          ))}
        </HStack>

        {/* Intro + simulator */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="What you are looking at" />
          <Text color="content.secondary" maxW="780px">
            The simulation below steps through a TLS handshake and then the extra mutual-TLS
            exchange. Press{' '}
            <Text as="span" fontWeight="medium">
              Play
            </Text>{' '}
            to run it automatically, or use{' '}
            <Text as="span" fontWeight="medium">
              Back
            </Text>{' '}
            /{' '}
            <Text as="span" fontWeight="medium">
              Next
            </Text>{' '}
            to drive it manually. Switch the scenario to a{' '}
            <Text as="span" fontWeight="medium">
              trusted
            </Text>{' '}
            or{' '}
            <Text as="span" fontWeight="medium">
              untrusted
            </Text>{' '}
            client to see how the broker accepts or rejects the connection.
          </Text>

          <Card.Root>
            <Card.Body p={6} mt="4" mb="4">
              <TlsHandshakeSimulation />
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Phases */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="The handshake, phase by phase"
            description="Each step in the simulator maps back to one of these phases."
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            {phases.map((p) => (
              <PhaseCard key={p.phase} {...p} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Key takeaways */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Key takeaways"
            description="What keystores, truststores, and mutual TLS actually guarantee."
          />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            {takeaways.map((t) => (
              <HStack key={t.title} gap={3} align="flex-start">
                <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
                  <Check size={12} color="var(--chakra-colors-green-600)" />
                </Box>
                <VStack align="start" gap={0}>
                  <Text fontWeight="medium">{t.title}</Text>
                  <Text fontSize="sm" color="content.secondary">
                    {t.description}
                  </Text>
                </VStack>
              </HStack>
            ))}
          </SimpleGrid>
        </VStack>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack gap={4} align="center" flexWrap="wrap">
              <Box p={3} bg="brand.solid" borderRadius="xl">
                <ShieldQuestion size={24} />
              </Box>
              <VStack align="start" gap={1}>
                <Heading size="lg">Trust is mutual, or it is nothing</Heading>
                <Text color="content.secondary">
                  Present a certificate, validate the other side's - that is the whole handshake.
                </Text>
              </VStack>
            </HStack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
